import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { PostHogProvider } from 'posthog-js/react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { HeaderProvider, useHeader } from './context/HeaderContext';
import ErrorBoundary from './components/ErrorBoundary';
import { config } from './config/env';
import MainLayout from './layouts/MainLayout';
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import TestGoals from './pages/TestGoals';
import GoalsList from './pages/GoalsList';
import ImportGoals from './pages/ImportGoals';
import GoalDetail from './pages/GoalDetail';
import LiveTestExecution from './pages/LiveTestExecution';
import InteractiveSession from './pages/InteractiveSession';
import InteractiveSessions from './pages/InteractiveSessions';
import TestResults from './pages/TestResults';
import TestCases from './pages/TestCases';
import ExecutionHistory from './pages/ExecutionHistory';
import Apps from './pages/Apps';
import AppDetail from './pages/AppDetail';
import Settings from './pages/Settings';
import Integrations from './pages/Integrations';
import LinearIntegration from './pages/LinearIntegration';
import SlackIntegration from './pages/SlackIntegration';
import JiraMappings from './pages/JiraMappings';
import ObservabilityIntegration from './pages/ObservabilityIntegration';
import ExecutionPlans from './pages/ExecutionPlans';
import ExecutionPlanDetail from './pages/ExecutionPlanDetail';
import ExecutionPlanEdit from './pages/ExecutionPlanEdit';
import LiveExecutionPlanProgress from './pages/LiveExecutionPlanProgress';
import Schedules from './pages/Schedules';
import ScheduleDetail from './pages/ScheduleDetail';
import SettingsLayout from './pages/settings/SettingsLayout';
import WorkspaceMembers from './pages/settings/WorkspaceMembers';
import GeneralSettings from './pages/settings/GeneralSettings';
import BillingSettings from './pages/settings/BillingSettings';
import ApiKeysSettings from './pages/settings/ApiKeysSettings';
import TestAssetsSettings from './pages/settings/TestAssetsSettings';
import ActiveDevicesSettings from './pages/settings/ActiveDevicesSettings';
import CategoriesSettings from './pages/settings/CategoriesSettings';
import AcceptInvite from './pages/AcceptInvite';
import CreateWorkspace from './pages/CreateWorkspace';
import NotFound from './pages/NotFound';

// Admin Page Imports
import { AdminAuthProvider } from './context/AdminAuthContext';
import AdminRoute from './components/AdminRoute';
import AdminLayout from './layouts/AdminLayout';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminWorkspaces from './pages/admin/AdminWorkspaces';
import AdminWorkspaceDetail from './pages/admin/AdminWorkspaceDetail';
import AdminApps from './pages/admin/AdminApps';
import AdminAppDetail from './pages/admin/AdminAppDetail';
import AdminSubscriptions from './pages/admin/AdminSubscriptions';
import AdminCoupons from './pages/admin/AdminCoupons';
import AdminActiveDevices from './pages/admin/AdminActiveDevices';
import AdminAdmins from './pages/admin/AdminAdmins';
import AdminAIUsage from './pages/admin/AdminAIUsage';
import AdminFeatureFlags from './pages/admin/AdminFeatureFlags';
import AdminGoals from './pages/admin/AdminGoals';
import AdminGoalDetail from './pages/admin/AdminGoalDetail';
import AdminRuns from './pages/admin/AdminRuns';
import AdminRunDetail from './pages/admin/AdminRunDetail';
import { ToastProvider } from './context/ToastContext';
import { FeatureFlagProvider, useFeatureFlags } from './context/FeatureFlagContext';
import './index.css';

// Protected route wrapper with MainLayout
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading, workspaces } = useAuth();
    const { headerLeft } = useHeader();

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: 'var(--bg-primary)'
            }}>
                <div className="spinner" style={{ width: 40, height: 40 }}></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" />;
    }

    // Redirect to workspace creation if user has no workspaces
    if (workspaces && workspaces.length === 0) {
        return <Navigate to="/create-workspace" />;
    }

    return <MainLayout headerLeft={headerLeft}>{children}</MainLayout>;
};

// Feature flag route guard wrapper
const FeatureFlagGuard = ({ flag, children }) => {
    const { isFeatureEnabled, loading } = useFeatureFlags();

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: 'var(--bg-primary)'
            }}>
                <div className="spinner" style={{ width: 40, height: 40 }}></div>
            </div>
        );
    }

    if (!isFeatureEnabled(flag)) {
        return <Navigate to="/" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />

            {config.AUTH_MODE === 'external' ? (
                <>
                    <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
                    <Route path="/reset-password/:token" element={<Navigate to="/login" replace />} />
                </>
            ) : (
                <>
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                </>
            )}

            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/create-workspace" element={<CreateWorkspace />} />

            {/* Protected Routes */}
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/test-goals"
                element={
                    <ProtectedRoute>
                        <TestGoals />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/test/execution/:id"
                element={
                    <ProtectedRoute>
                        <LiveTestExecution />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/interactive-session/:sessionId"
                element={
                    <ProtectedRoute>
                        <FeatureFlagGuard flag="enable-live-sessions">
                            <InteractiveSession />
                        </FeatureFlagGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/interactive-sessions"
                element={
                    <ProtectedRoute>
                        <FeatureFlagGuard flag="enable-live-sessions">
                            <InteractiveSessions />
                        </FeatureFlagGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/test/results/:id"
                element={
                    <ProtectedRoute>
                        <TestResults />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/goals"
                element={
                    <ProtectedRoute>
                        <GoalsList />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/goals/import"
                element={
                    <ProtectedRoute>
                        <ImportGoals />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/goals/:id"
                element={
                    <ProtectedRoute>
                        <GoalDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/executions"
                element={
                    <ProtectedRoute>
                        <ExecutionHistory />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/test-cases"
                element={
                    <ProtectedRoute>
                        <TestCases />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/apps"
                element={
                    <ProtectedRoute>
                        <Apps />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/apps/:id"
                element={
                    <ProtectedRoute>
                        <AppDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/apps/:id"
                element={
                    <ProtectedRoute>
                        <AppDetail />
                    </ProtectedRoute>
                }
            />

            {/* Settings Module */}
            <Route
                path="/settings"
                element={
                    <ProtectedRoute>
                        <SettingsLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to="general" replace />} />
                <Route path="general" element={<GeneralSettings />} />
                <Route path="members" element={<WorkspaceMembers />} />
                <Route path="roles" element={<GeneralSettings />} /> {/* Placeholder */}
                <Route path="billing" element={<BillingSettings />} />
                <Route path="integrations" element={<Integrations />} />
                <Route path="api-keys" element={<ApiKeysSettings />} />
                <Route path="test-assets" element={<TestAssetsSettings />} />
                <Route path="devices" element={<ActiveDevicesSettings />} />
                <Route path="categories" element={<CategoriesSettings />} />
            </Route>

            {/* Legacy direct routes - verify if needed or redirect */}
            <Route path="/api-keys" element={<Navigate to="/settings/api-keys" replace />} />
            <Route path="/integrations" element={<Navigate to="/settings/integrations" replace />} />

            <Route
                path="/integrations/linear"
                element={
                    <ProtectedRoute>
                        <LinearIntegration />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/integrations/slack"
                element={
                    <ProtectedRoute>
                        <SlackIntegration />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/integrations/jira"
                element={
                    <ProtectedRoute>
                        <JiraMappings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/integrations/observability"
                element={
                    <ProtectedRoute>
                        <ObservabilityIntegration />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/execution-plans"
                element={
                    <ProtectedRoute>
                        <ExecutionPlans />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/execution-plans/:id"
                element={
                    <ProtectedRoute>
                        <ExecutionPlanDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/execution-plans/:id/edit"
                element={
                    <ProtectedRoute>
                        <ExecutionPlanEdit />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/execution-plans/executions/:id"
                element={
                    <ProtectedRoute>
                        <LiveExecutionPlanProgress />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/schedules"
                element={
                    <ProtectedRoute>
                        <Schedules />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/schedules/:id"
                element={
                    <ProtectedRoute>
                        <ScheduleDetail />
                    </ProtectedRoute>
                }
            />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />

            <Route
                path="/admin/*"
                element={
                    <AdminRoute>
                        <AdminLayout>
                            <Routes>
                                <Route index element={<Navigate to="dashboard" replace />} />
                                <Route path="dashboard" element={<AdminDashboard />} />
                                <Route path="users" element={<AdminUsers />} />
                                <Route path="users/:id" element={<AdminUserDetail />} />
                                <Route path="workspaces" element={<AdminWorkspaces />} />
                                <Route path="workspaces/:id" element={<AdminWorkspaceDetail />} />
                                <Route path="apps" element={<AdminApps />} />
                                <Route path="apps/:id" element={<AdminAppDetail />} />
                                <Route path="subscriptions" element={<AdminSubscriptions />} />
                                <Route path="ai-usage" element={<AdminAIUsage />} />
                                <Route path="coupons" element={<AdminCoupons />} />
                                <Route path="feature-flags" element={<AdminFeatureFlags />} />
                                <Route path="goals" element={<AdminGoals />} />
                                <Route path="goals/:id" element={<AdminGoalDetail />} />
                                <Route path="runs" element={<AdminRuns />} />
                                <Route path="runs/:id" element={<AdminRunDetail />} />
                                <Route path="devices" element={<AdminActiveDevices />} />
                                <Route path="admins" element={<AdminAdmins />} />
                                <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                            </Routes>
                        </AdminLayout>
                    </AdminRoute>
                }
            />

            {/* 404 Catch-all route - must be last */}
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}

function App() {
    return (
        <PostHogProvider client={window.posthog}>
            <BrowserRouter>
                <ToastProvider>
                    <AdminAuthProvider>
                        <AuthProvider>
                                <WorkspaceProvider>
                                    <FeatureFlagProvider>
                                        <HeaderProvider>
                                            <AppRoutes />
                                        </HeaderProvider>
                                    </FeatureFlagProvider>
                                </WorkspaceProvider>
                        </AuthProvider>
                    </AdminAuthProvider>
                </ToastProvider>
            </BrowserRouter>
        </PostHogProvider>
    );
}

export default App;
